import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import React, { lazy, memo, Suspense, useMemo } from 'react'
import { CardContentProps } from './CardContent'

const LazyActionButtons = lazy(() =>
    import('./ActionButtons').then((module) => ({
        default: module.ActionButtons,
    }))
)
const LazyUserAvatarAndDetails = lazy(() =>
    import('./UserAvatarAndDetails').then((module) => ({
        default: module.UserAvatarAndDetails,
    }))
)

/**
 * Header subComponent for the track change card.
 * Renders user avatar, details, and action buttons if applicable.
 * @param {CardContentProps} props - The component props.
 * @returns {JSX.Element} The rendered header for the track change card.
 */
export const TrackChangeCardHeader = memo(
    ({ trackChanges, cardSuggestion, classes, cardType }: CardContentProps) => {
        const contentItemViewNodes = useShallowSelector(
            (s) => s.contentItemViewNodes
        )

        // A UOM track change targets an element with type 'UnitsOfMeasure'.
        // Search all nodes' elements to find the matching element by ID,
        // since the contentItemId key may not align with the node's id field.
        // Accept/reject is not applicable for UOM edits.
        const isUOMTrackChange = useMemo(() => {
            const elementId = trackChanges?.contentItemElementId
            if (!elementId) return false
            return contentItemViewNodes.some((node) =>
                node.elements?.some(
                    (el) =>
                        el.id === elementId && el.type === 'UnitsOfMeasure'
                )
            )
        }, [
            trackChanges?.contentItemElementId,
            contentItemViewNodes,
        ])

        const shouldShowActionButtons = useMemo(() => {
            if (!trackChanges) return false

            // List all cardType flags that should suppress action buttons
            const hideFlags = [
                cardType.isApprovedAdded,
                cardType.isRejectedAdded,
                cardType.isApprovedDeleted,
                cardType.isRejectedDeleted,
                cardType.isApprovedOptionGroup,
                cardType.isRejectedOptionGroup,
                cardType.isOptionGroup,
                cardType.isModifiedApproved,
                cardType.isModifiedRejected,
                cardType.isReplacedApproved,
                cardType.isReplacedRejected,
                isUOMTrackChange,
            ]

            // If any suppression flag is true, we hide the buttons
            return !hideFlags.some(Boolean)
        }, [trackChanges, cardType, isUOMTrackChange])
        const userImageUrl = useMemo(
            () => trackChanges?.userProfileInfo?.imageUrl,
            [trackChanges?.userProfileInfo?.imageUrl]
        )

        return (
            <Suspense fallback={<div className={classes?.placeholderHeader} />}>
                <LazyUserAvatarAndDetails
                    imageUrl={userImageUrl}
                    trackChanges={trackChanges}
                    cardSuggestion={cardSuggestion}
                    classes={classes}
                    shouldShowActionButtons={shouldShowActionButtons}
                >
                    {shouldShowActionButtons && (
                        <LazyActionButtons
                            cardSuggestion={cardSuggestion}
                            trackChanges={trackChanges}
                            classes={classes}
                        />
                    )}
                </LazyUserAvatarAndDetails>
            </Suspense>
        )
    }
)

TrackChangeCardHeader.displayName = 'TrackChangeCardHeader'
